import {
  Callout,
  DataTable,
  Lettered,
  Ph,
  Pill,
  Plain,
  Term,
} from "./legalBlocks";

export const PRIVACY_GLANCE = [
  ["Who controls your data", <>Sayaty Enterprise, registered in Ghana.</>],
  [
    "We never sell your data",
    <>
      We do not sell personal data, and we do not share it with data brokers or
      advertising networks.
    </>,
  ],
  [
    "Card details",
    <>
      Full card numbers never touch our servers. Card and mobile money payments are
      handled inside Paystack.
    </>,
  ],
  [
    "Location",
    <>
      Used only while the app is open, to find suppliers near you and to place a
      delivery. Never in the background.
    </>,
  ],
  [
    "Your suppliers see",
    <>
      Your name, phone number, delivery address and order contents — the minimum needed
      to deliver.
    </>,
  ],
  [
    "Your rights",
    <>
      Access, correction, objection, restriction and erasure under the Data Protection
      Act, 2012 (Act 843).
    </>,
  ],
];

const PRIVACY_SECTIONS = [
  {
    id: "p1",
    n: "1",
    title: "Who we are and how to reach us",
    body: (
      <>
        <p>
          Sayagie is an on-demand marketplace that connects customers in Ghana with
          licensed suppliers of liquefied petroleum gas (LPG), cylinders, accessories and
          related products, and arranges for those products to be delivered to an address
          you choose.
        </p>
        <p>
          The data controller responsible for the personal data described in this policy
          is Sayaty Enterprise, a company incorporated in Ghana with registration number{" "}
          <Ph>[company reg. no.]</Ph>, whose registered office is at{" "}
          <Ph>[registered office address]</Ph>. In this policy, <Term>Sayagie</Term>,{" "}
          <Term>we</Term>, <Term>us</Term> and <Term>our</Term> mean that company.
        </p>
        <p>
          For anything to do with your personal data — a question, a request, or a
          complaint — write to <Ph>[privacy email]</Ph>. If you have appointed a Data
          Protection Supervisor under section 58 of Act 843, name them here:{" "}
          <Ph>[supervisor name and contact]</Ph>.
        </p>
      </>
    ),
  },
  {
    id: "p2",
    n: "2",
    title: "What this policy covers",
    body: (
      <>
        <p>
          This policy covers the Sayagie mobile apps for Android and iOS, the Sayagie
          website, and any Sayagie account, order, wallet or support interaction connected
          to them. Together we call these the <Term>Services</Term>.
        </p>
        <p>
          It does not cover the independent suppliers you order from. Once a supplier
          receives your order, that supplier becomes a separate controller of the order
          details it needs to fulfil the delivery, and its own privacy practices apply to
          what it does with them. It also does not cover third-party sites you reach
          through a link in the app, including your bank’s pages during a payment.
        </p>
      </>
    ),
  },
  {
    id: "p3",
    n: "3",
    title: "The personal data we collect",
    body: (
      <>
        <p>
          We collect only what the Services need to work. The table below lists every
          category we hold, what it contains, and where it comes from.
        </p>
        <DataTable
          head={["Category", "What it includes", "Where it comes from"]}
          rows={[
            [
              "Account",
              "Full name, phone number, email address, password (stored only as a salted hash), profile photo, date of birth where you provide it, account status.",
              "You, at sign-up and in Edit Profile.",
            ],
            [
              "Sign-in identity",
              "The Google or Apple account identifier and verified email you sign in with, plus your name where that provider releases it.",
              "Google Sign-In or Sign in with Apple, when you choose them.",
            ],
            [
              "Verification",
              "One-time codes sent to your phone or email, and the timestamp of each verification attempt.",
              "Generated by us; delivered by our SMS and email providers.",
            ],
            [
              "Addresses",
              "Saved delivery addresses, labels, landmarks and delivery notes, with the map coordinates you pin for each.",
              "You, plus the device location you choose to use.",
            ],
            [
              "Location",
              "Approximate or precise device coordinates while the app is in the foreground.",
              "Your device, only after you grant permission.",
            ],
            [
              "Orders",
              "Items and quantities, cylinder sizes, supplier, price breakdown, delivery address and window, order status history, invoices and receipts.",
              "Generated as you order.",
            ],
            [
              "Payments",
              "Payment method chosen, amount, currency, transaction references and status, the last four digits and card brand of a saved card, and the authorisation token Paystack issues for reuse.",
              <>
                You and Paystack.{" "}
                <strong>
                  We never receive or store a full card number, CVV or your mobile money
                  PIN.
                </strong>
              </>,
            ],
            [
              "Wallet",
              "Wallet balances by currency, top-ups, deductions, refunds and the running transaction ledger.",
              "Generated as you transact.",
            ],
            [
              "Payout details",
              "For withdrawals: account holder name, bank or mobile money provider, and account or wallet number. We display these masked.",
              "You, when you add a withdrawal account.",
            ],
            [
              "Reviews",
              "Star ratings and written reviews of a supplier, tied to the completed order they relate to, and shown publicly with your display name and photo.",
              "You, after an order completes.",
            ],
            [
              "Media",
              "Photos or documents you upload — a profile picture, or an attachment supporting a complaint or withdrawal request.",
              "Your camera or photo library, when you choose a file.",
            ],
            [
              "Device & technical",
              "Device model, operating system version, app version, language, time zone, IP address, a device identifier, and the Firebase push token for your installation.",
              "Collected automatically when the app runs.",
            ],
            [
              "Support",
              "Messages you send us, the contents of a dispute or refund request, and our replies.",
              "You, when you contact us.",
            ],
          ]}
        />
        <Callout title="Biometrics stay on your device">
          If you turn on Face ID, Touch ID or fingerprint unlock, the match happens
          entirely inside your device’s secure hardware. Your fingerprint or face data is
          never sent to Sayagie and we never see it — the device simply tells the app
          whether the unlock succeeded.
        </Callout>
      </>
    ),
  },
  {
    id: "p4",
    n: "4",
    title: "Device permissions the app asks for",
    body: (
      <>
        <p>
          Each permission is requested at the moment the feature needs it, and you can
          refuse or withdraw any of them in your device settings. Refusing a permission
          disables the feature it supports; it does not block the rest of the app.
        </p>
        <DataTable
          head={["Permission", "What it is used for", "Needed?"]}
          rows={[
            [
              "Location",
              "Finding suppliers near you, pinning a delivery address on the map, and estimating a delivery time. Foreground only — the app never tracks you in the background.",
              <Pill>Optional</Pill>,
            ],
            [
              "Camera",
              "Taking a profile photo, or photographing a cylinder or meter to support a complaint.",
              <Pill>Optional</Pill>,
            ],
            [
              "Photos & media",
              "Choosing an existing image to upload, and saving an invoice or receipt to your device.",
              <Pill>Optional</Pill>,
            ],
            [
              "Microphone",
              "Recording a voice or video note on a support request.",
              <Pill>Optional</Pill>,
            ],
            [
              "Notifications",
              "Order status updates, delivery alerts, wallet and withdrawal confirmations, and service notices.",
              <Pill>Optional</Pill>,
            ],
            [
              "Biometrics",
              "Unlocking the app and confirming sensitive actions without retyping your password.",
              <Pill>Optional</Pill>,
            ],
            [
              "Internet",
              "Communicating with Sayagie’s servers. Without it the app cannot function.",
              <Pill required>Required</Pill>,
            ],
          ]}
        />
      </>
    ),
  },
  {
    id: "p5",
    n: "5",
    title: "Why we use your data, and on what legal basis",
    body: (
      <>
        <p>
          Under Act 843 we must have a lawful basis for every use. Ours are set out below.
        </p>
        <DataTable
          head={["Purpose", "Data used", "Lawful basis"]}
          rows={[
            [
              "Create and secure your account",
              "Account, sign-in identity, verification, device",
              "Performance of our contract with you",
            ],
            [
              "Show nearby suppliers and prices",
              "Location, addresses",
              "Your consent (location), then contract",
            ],
            [
              "Take, route and deliver an order",
              "Orders, addresses, account",
              "Performance of our contract with you",
            ],
            [
              "Take payment and run the wallet",
              "Payments, wallet, account",
              "Performance of our contract; legal obligation",
            ],
            [
              "Pay out withdrawals",
              "Payout details, wallet",
              "Performance of our contract; legal obligation",
            ],
            [
              "Send order and account notifications",
              "Device, orders, account",
              "Performance of our contract with you",
            ],
            [
              "Send offers and marketing",
              "Account, orders",
              "Your consent, withdrawable at any time",
            ],
            ["Publish your reviews", "Reviews, account", "Your consent, given when you post"],
            [
              "Prevent fraud and abuse",
              "Device, payments, orders",
              "Our legitimate interest in a safe marketplace",
            ],
            [
              "Keep tax, accounting and transaction records",
              "Orders, payments, wallet",
              "Legal obligation",
            ],
            [
              "Resolve disputes and defend claims",
              "Any relevant category",
              "Our legitimate interest; establishment of legal claims",
            ],
            [
              "Fix faults and improve the Services",
              "Device, technical, aggregated usage",
              "Our legitimate interest in a working product",
            ],
          ]}
        />
        <p>
          Where we rely on consent you may withdraw it at any time, and where we rely on
          legitimate interests you may object — see section 12.
        </p>
      </>
    ),
  },
  {
    id: "p6",
    n: "6",
    title: "Payments, cards and the wallet",
    body: (
      <>
        <p>
          Card and mobile money payments are processed by{" "}
          <Term>Paystack Payments Limited</Term>. When you pay, the app opens a
          Paystack-hosted checkout. You enter your card number, CVV, PIN or mobile money
          authorisation on Paystack’s page, not on ours. Paystack returns to us only a
          transaction reference, a status, the amount, and — if you asked to save the
          method — a reusable authorisation token plus the card brand and last four
          digits.
        </p>
        <p>
          This means we cannot charge a saved card outside the flows you authorise, and a
          Sayagie data breach cannot expose your full card number, because we do not hold
          one.
        </p>
        <p>
          Your Sayagie wallet is a prepaid balance we maintain in our own records. It is
          not a bank account, it is not a deposit, and it does not earn interest. We keep
          a full ledger of every top-up, payment, refund and withdrawal against it,
          because we are required to and because you need to be able to check it.
        </p>
        <p>
          When you add a withdrawal account we send the account holder name, provider and
          account number to Paystack so it can create a transfer recipient and move funds
          to you. We store the same details, masked in the interface, so you can reuse
          them.
        </p>
      </>
    ),
  },
  {
    id: "p7",
    n: "7",
    title: "Who we share your data with",
    body: (
      <>
        <p>
          We do not sell personal data, and we do not share it with advertising networks
          or data brokers. We share it only in these situations.
        </p>
        <DataTable
          head={["Recipient", "What they receive", "Why"]}
          rows={[
            [
              "The supplier you order from",
              "Your name, phone number, delivery address and notes, and the contents of that order",
              "So they can prepare and deliver it",
            ],
            [
              "Delivery rider",
              "Your first name, phone number and delivery address for the active order",
              "So they can find you and hand over the goods",
            ],
            [
              "Paystack",
              "Amount, currency, your email, and payout account details where relevant",
              "Payment processing and transfers",
            ],
            [
              "Google Firebase",
              "Device push token and message payload",
              "Delivering push notifications",
            ],
            [
              "Google / Apple",
              "Only the sign-in exchange you initiate",
              "Authenticating you when you use social sign-in",
            ],
            [
              "SMS and email providers",
              "Your phone number or email and the message content",
              "Sending verification codes, receipts and notices",
            ],
            [
              "Map and geocoding provider",
              "Coordinates or a search string",
              "Rendering maps and turning a pin into an address",
            ],
            [
              "Hosting and infrastructure",
              "Data at rest and in transit on our servers",
              "Running the Services",
            ],
            [
              "Professional advisers",
              "Only what is relevant",
              "Audit, accounting and legal advice",
            ],
            [
              "Regulators, courts, law enforcement",
              "Only what we are lawfully required to disclose",
              "Legal obligation or defence of claims",
            ],
            [
              "An acquirer",
              "Account and transaction records",
              "If the business is sold or reorganised; we will tell you first",
            ],
          ]}
        />
        <p>
          Every service provider above acts on our documented instructions under a written
          processing agreement, and may not use your data for its own purposes.
        </p>
      </>
    ),
  },
  {
    id: "p8",
    n: "8",
    title: "Location data in particular",
    body: (
      <>
        <p>
          Location is the permission people ask about most, so to be precise: Sayagie
          requests location only while the app is open and you are looking at a screen
          that needs it — the nearby suppliers list, the map address picker, or an active
          delivery. We do not request background or “always” location, and the app does
          not build a movement history.
        </p>
        <p>
          We cache your most recent location fix on your device so the nearby list loads
          instantly the next time you open the app. Clearing the app’s storage or signing
          out removes that cache.
        </p>
        <p>
          If you decline the permission you can still use Sayagie: type or pick a delivery
          address manually instead.
        </p>
      </>
    ),
  },
  {
    id: "p9",
    n: "9",
    title: "Notifications and marketing",
    body: (
      <>
        <p>There are two kinds of message, and they are governed differently.</p>
        <p>
          <Term>Service messages</Term> tell you something you need to know about an
          order, a payment, a withdrawal or your account’s security. They are part of the
          service and we send them as long as you hold an account. Turning off the
          operating system permission stops the push, but we may still reach you by SMS or
          email for a critical notice.
        </p>
        <p>
          <Term>Marketing messages</Term> — offers, promotions, new suppliers — go out
          only if you have opted in. Every one carries a way to stop, and you can turn
          them off at any time in Account Settings without affecting service messages.
        </p>
      </>
    ),
  },
  {
    id: "p10",
    n: "10",
    title: "How long we keep things",
    body: (
      <DataTable
        head={["Record", "Retention period"]}
        rows={[
          ["Active account", "For as long as your account is open."],
          [
            "Deactivated account",
            <>
              <Ph>[retention window]</Ph> after deactivation, so you can reactivate; then
              anonymised or erased, except where a row below requires otherwise.
            </>,
          ],
          [
            "Orders, invoices, wallet ledger",
            "Six years from the end of the relevant tax year, to meet Ghanaian tax and accounting record-keeping requirements.",
          ],
          [
            "Payment and payout records",
            "Six years, for the same reason and for chargeback and anti-fraud purposes.",
          ],
          [
            "Published reviews",
            "Kept while published. If you delete a review or close your account, we remove your name and photo from it; the rating may remain in a supplier’s aggregate score.",
          ],
          [
            "Support conversations",
            "Three years from the last message, or longer if the matter is in dispute.",
          ],
          [
            "Device and technical logs",
            <>
              <Ph>[log retention, e.g. 90 days]</Ph>.
            </>,
          ],
          [
            "Marketing consent records",
            "For as long as you are opted in, plus the period needed to prove the consent was given.",
          ],
        ]}
      />
    ),
  },
  {
    id: "p11",
    n: "11",
    title: "How we protect your data",
    body: (
      <>
        <Plain
          items={[
            "All traffic between the app and our servers runs over TLS; the app rejects plaintext connections.",
            "Passwords are stored only as salted hashes — we cannot read yours, which is why a reset replaces it rather than revealing it.",
            "Sessions use short-lived access tokens with refresh tokens held in the device keychain or keystore, and can be revoked centrally.",
            "Optional biometric unlock adds a device-level lock on top of your session.",
            "Access to production data is limited to staff who need it for their role, and is logged.",
            "Card data is out of scope by design, because Paystack handles it and we never receive it.",
          ]}
        />
        <p>
          No system is perfectly secure. If a breach occurs that is likely to cause you
          harm, we will notify the Data Protection Commission and you, without undue
          delay, and tell you plainly what happened and what to do about it.
        </p>
      </>
    ),
  },
  {
    id: "p12",
    n: "12",
    title: "Your rights over your data",
    body: (
      <>
        <p>
          Under the Data Protection Act, 2012 (Act 843) you have the right to:
        </p>
        <Lettered
          items={[
            "be told whether we hold personal data about you, and to receive a copy of it;",
            "have inaccurate or incomplete data corrected — much of this you can do yourself in Edit Profile;",
            "object to processing that is likely to cause you unwarranted damage or distress;",
            "stop processing for direct marketing at any time;",
            "ask us to block, erase or destroy data we hold unlawfully, or that we no longer need;",
            "not be subject to a decision based solely on automated processing that significantly affects you; and",
            "complain to the Data Protection Commission.",
          ]}
        />
        <p>
          If you are in the European Economic Area or the United Kingdom, we also honour
          the equivalent rights under the GDPR, including data portability.
        </p>
        <p>
          To exercise any of these, write to <Ph>[privacy email]</Ph> from the email
          address on your account, or use the options in Account Settings. We will respond
          within <strong>30 days</strong>. We do not charge for a reasonable request. We
          may ask you to confirm your identity before we act, because handing your data to
          the wrong person would be the greater harm.
        </p>
      </>
    ),
  },
  {
    id: "p13",
    n: "13",
    title: "Deactivating and deleting your account",
    body: (
      <>
        <Callout title="Read this before you tap Delete">
          Choosing to delete your account in the app currently <strong>deactivates</strong>{" "}
          it. Your profile is hidden, you are signed out, notifications stop, and your
          data is retained so that you can reactivate by signing in again. It is not yet
          an immediate erasure.
        </Callout>
        <p>
          If you want your personal data actually erased rather than deactivated, email{" "}
          <Ph>[privacy email]</Ph> and ask for erasure. We will erase or irreversibly
          anonymise everything we are not legally required to keep, and tell you what
          remains and why — in practice that means the order, invoice and payment records
          described in section 10, which we must retain for tax and accounting purposes
          and which we strip of identifying details as soon as that period allows.
        </p>
        <p>
          Erasure cannot be undone, and any remaining wallet balance should be withdrawn
          first.
        </p>
      </>
    ),
  },
  {
    id: "p14",
    n: "14",
    title: "Children",
    body: (
      <p>
        Sayagie is not for children. LPG is a controlled and hazardous product and the
        Services are intended only for people aged 18 or over. We do not knowingly collect
        personal data from anyone under 18. If you believe a child has created an account,
        tell us at <Ph>[privacy email]</Ph> and we will close it and erase the data.
      </p>
    ),
  },
  {
    id: "p15",
    n: "15",
    title: "Cookies on the website",
    body: (
      <p>
        The website uses cookies and similar storage to keep you signed in, remember
        preferences and measure usage. Strictly necessary cookies are always on; the rest
        run only with your consent, which you can change at any time from{" "}
        <Ph>[cookie settings link]</Ph>. The mobile apps use device storage rather than
        cookies for the same purposes.
      </p>
    ),
  },
  {
    id: "p16",
    n: "16",
    title: "Where your data is processed",
    body: (
      <>
        <p>
          Sayagie operates in Ghana, but some of our service providers — hosting, push
          notifications, email and payments — process data on servers outside Ghana,
          including in the European Union and the United States.
        </p>
        <p>
          When we transfer personal data outside Ghana we do so only where the receiving
          country offers an adequate level of protection, or under a written contract
          imposing equivalent safeguards, as required by sections 18 and 47 of Act 843.
          You can ask us for details of the safeguards applying to a specific transfer.
        </p>
      </>
    ),
  },
  {
    id: "p17",
    n: "17",
    title: "Changes to this policy",
    body: (
      <p>
        We update this policy when the Services change or the law does. The version number
        and date at the top always reflect the current text. For any change that
        materially affects your rights or how we use your data, we will notify you in the
        app or by email at least <strong>14 days</strong> before it takes effect, so you
        can object or close your account. Continuing to use Sayagie after a change takes
        effect means the updated policy applies to you.
      </p>
    ),
  },
  {
    id: "p18",
    n: "18",
    title: "Contact and complaints",
    body: (
      <>
        <p>
          Raise it with us first — most things are quicker to fix directly. If we cannot
          resolve it, you are entitled to complain to Ghana’s supervisory authority.
        </p>
        <DataTable
          head={["Channel", "Where to write"]}
          rows={[
            [
              "Privacy enquiries",
              <>
                <Ph>[privacy email]</Ph>, <Ph>[registered office address]</Ph>
              </>,
            ],
            [
              "General support",
              <>
                <Ph>[support email]</Ph>, <Ph>[support phone]</Ph>
              </>,
            ],
            ["Supervisory authority", "Data Protection Commission, Ghana — dataprotection.org.gh"],
          ]}
        />
      </>
    ),
  },
  {
    id: "p19",
    n: "19",
    title: "Before you publish",
    internal: true,
    body: (
      <>
        <p>Fill each of these, then delete this section:</p>
        <Plain
          items={[
            "Company registration number and registered office address (1)",
            "Privacy email, used in clauses 1, 12, 13, 14 and 18",
            "Data Protection Supervisor name and contact, if one is appointed (1)",
            "Deactivated-account retention window and log retention period (10)",
            "Cookie settings link (15)",
            "Support email and phone (18)",
            "Effective date (masthead)",
            "Confirm clause 13 still matches what the backend does — Delete Account currently deactivates",
          ]}
        />
      </>
    ),
  },
];

export default PRIVACY_SECTIONS;
